const QUARTER_END_MAP: Record<number, { month: number; day: number }> = {
  1: { month: 3, day: 31 },
  2: { month: 6, day: 30 },
  3: { month: 9, day: 30 },
  4: { month: 12, day: 31 },
};

const QUARTER_START_MAP: Record<number, { month: number; day: number }> = {
  1: { month: 1, day: 1 },
  2: { month: 4, day: 1 },
  3: { month: 7, day: 1 },
  4: { month: 10, day: 1 },
};

const parseQuarterSlug = (slug?: string | null) => {
  if (!slug) {
    return null;
  }

  const match = /^(\d{4})-Q([1-4])$/.exec(slug);
  if (!match) {
    return null;
  }

  return {
    year: Number(match[1]),
    quarter: Number(match[2]),
  };
};

const formatDate = (year: number, month: number, day: number) => {
  const formattedMonth = String(month).padStart(2, '0');
  const formattedDay = String(day).padStart(2, '0');
  return `${year}-${formattedMonth}-${formattedDay}`;
};

export const getQuarterEndDate = (slug?: string | null) => {
  const parsed = parseQuarterSlug(slug);
  if (!parsed) {
    return null;
  }

  const quarterInfo = QUARTER_END_MAP[parsed.quarter];
  if (!quarterInfo) {
    return null;
  }

  const { month, day } = quarterInfo;
  return formatDate(parsed.year, month, day);
};

export const getQuarterStartDate = (slug?: string | null) => {
  const parsed = parseQuarterSlug(slug);
  if (!parsed) {
    return null;
  }

  const quarterInfo = QUARTER_START_MAP[parsed.quarter];
  if (!quarterInfo) {
    return null;
  }

  const { month, day } = quarterInfo;
  return formatDate(parsed.year, month, day);
};

/**
 * When applying the QuarterData Filter to Incentives, the logic we use is:
 * - when "Most Recent" option is selected in QuarterFilter, we should only limit Incentives start date,
 * - while selecting older quarters, then show Incentives from that quarter's first day through (but not including)
 *   the next chronologically newer quarter's first day. This aligns with the backend's less-than end-date filtering.
 */
export const getIncentiveQuarterDateRange = (
  selectedQuarterSlug: string | null | undefined,
  quarterSlugs: string[],
  isLatestQuarterSelected: boolean
) => {
  const slug = selectedQuarterSlug ?? null;
  const startDate = getQuarterStartDate(slug) ?? undefined;

  if (!slug || !startDate) {
    return { startDate: undefined, endDate: undefined };
  }

  if (isLatestQuarterSelected) {
    return { startDate, endDate: undefined };
  }

  const index = quarterSlugs.findIndex((candidate) => candidate === slug);
  if (index <= 0) {
    return { startDate, endDate: undefined };
  }

  const nextFutureQuarterSlug = quarterSlugs[index - 1];
  const nextFutureQuarterStart = getQuarterStartDate(nextFutureQuarterSlug);
  if (!nextFutureQuarterStart) {
    return { startDate, endDate: undefined };
  }

  const endDate = nextFutureQuarterStart ?? undefined;
  return {
    startDate,
    endDate,
  };
};

export default getQuarterEndDate;
